import {makeAutoObservable} from 'mobx'
import {DataObjectId, Hub, PaymentStatus} from 'yougile-model'
import {PaymentClient} from '@client/payment-client'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {Tracking, TrackingEvent} from '@common/tracking'
import {createUseStore} from '@containers/hooks'
import {Company} from '@extensions/company'
import {globals} from '@extensions/globals-base'
import {Page} from '@extensions/page'
import {PageService} from '@extensions/page-service'
import {Project} from '@extensions/project'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {PersonalChatProxy} from '@extensions/proxies/personal-chat-proxy'
import {ProjectProxy} from '@extensions/proxies/project-proxy'
import {mainMenuLocalStorageKey} from './constants'
import {
  allMyTasks,
  getCompanies,
  getCompanyPreferencesForCurrentUser,
  getProjects,
  getUserRegisterTime,
  getUsersAndGroups,
  isDesktopBannerAllowed,
  isMobileBannerAllowed
} from './handlers/sync-handlers'

export interface MainMenuCompany extends Company {
  newMsg: number
}
export type MainMenuUsersAndGroup = Array<PersonalChatProxy | GroupChatProxy>

interface State {
  companies: MainMenuCompany[]
  projects: ProjectProxy[]
  usersAndGroups: MainMenuUsersAndGroup
  desktopBannerAllowed: boolean
  mobileBannerAllowed: boolean
  canSeeStatsPage: boolean | undefined
  showBilling: boolean
  canCreateGroupChat: boolean
  canSeeLicensesPage: boolean
  readonly: boolean
  paymentStatus: PaymentStatus
  currentPage: Page | undefined
  currentProject: Hub | undefined
  myTasksCount: number
  myTasksUnseenCount: number
  userRegisterTime: number
  newMsgInOtherCompanies: number
  menuOpen?: boolean
}

export class MainMenuStore {
  state: State = {
    companies: [],
    projects: [],
    usersAndGroups: [],
    desktopBannerAllowed: false,
    mobileBannerAllowed: false,
    canSeeStatsPage: undefined,
    showBilling: false,
    canCreateGroupChat: false,
    canSeeLicensesPage: false,
    readonly: true,
    paymentStatus: PaymentClient.get(globals.getCompanyId()),
    currentPage: undefined,
    currentProject: undefined,
    myTasksCount: 0,
    myTasksUnseenCount: 0,
    userRegisterTime: 0,
    newMsgInOtherCompanies: 0,
    menuOpen: undefined
  }

  constructor() {
    makeAutoObservable(this)

    const menuOptionsStr = window.localStorage.getItem(mainMenuLocalStorageKey)
    const menuOptions = menuOptionsStr && JSON.parse(menuOptionsStr)
    this.state.menuOpen = menuOptions ? !!menuOptions?.menuOpen : true

    void Tracking.sendUserProperty(
      {
        leftMenuState: this.state.menuOpen ? 'expanded' : 'collapsed'
      },
      true
    )
  }

  syncStateOnCompanyChanged(): void {
    this.state.companies = getCompanies()
    this.state.projects = getProjects()
    this.state.usersAndGroups = getUsersAndGroups()

    const {myTasksCount, myTasksUnseenCount} = allMyTasks()
    this.state.myTasksCount = myTasksCount
    this.state.myTasksUnseenCount = myTasksUnseenCount
    const {canCreateGroupChat, canSeeLicensesPage, canSeeStatsPage} =
      getCompanyPreferencesForCurrentUser()
    this.state.canCreateGroupChat = canCreateGroupChat
    this.state.canSeeLicensesPage = canSeeLicensesPage
    this.state.canSeeStatsPage = canSeeStatsPage
    this.state.paymentStatus = PaymentClient.get(globals.getCompanyId())
    this.state.currentPage = PageService.getPage()
    this.state.currentProject = PageService.getCurrentProject()

    this.state.showBilling = Boolean(Backend.features.payment)
    this.state.readonly = globals.readonly

    this.state.newMsgInOtherCompanies = this.state.companies
      .filter(c => c.id !== globals.getCompanyId())
      .map(c => c.newMsg)
      .reduce((a, b) => a + b, 0)
  }

  syncStateOnProfileChanged(): void {
    this.state.usersAndGroups = getUsersAndGroups()
    this.state.desktopBannerAllowed = isDesktopBannerAllowed()
    this.state.mobileBannerAllowed = isMobileBannerAllowed()
    this.state.userRegisterTime = getUserRegisterTime()

    this.state.showBilling = Boolean(Backend.features.payment)
    this.state.readonly = globals.readonly
  }

  onProjectReorder = (
    projectId: DataObjectId,
    beforeId: DataObjectId | 'bottom'
  ): void => {
    const box = Sync.getCurrentSandbox()
    Project.fixOrderIfNeeded(box.companyId)
    if (projectId === beforeId) {
      return
    }
    const order = Project.getOrder(box.companyId)
    const oldIx = order.indexOf(projectId)
    if (oldIx > -1) {
      order.splice(oldIx, 1)
    }
    if (beforeId === 'bottom') {
      Project.setOrder(box.companyId, [...order, projectId])
      this.state.projects = getProjects()
    } else {
      const ix = Math.max(0, order.indexOf(beforeId))
      order.splice(ix, 0, projectId)
      Project.setOrder(box.companyId, order)
      this.state.projects = getProjects()
    }
  }

  setMenuOpen(isOpened: boolean): void {
    this.state.menuOpen = isOpened
    void Tracking.send(
      isOpened
        ? TrackingEvent.leftPanelExpandPanelButtonClick
        : TrackingEvent.leftPanelCollapsePanelButtonClick
    )
    void Tracking.sendUserProperty({
      leftMenuState: isOpened ? 'expanded' : 'collapsed'
    })

    setTimeout(() => {
      const prevValueStr = window.localStorage.getItem(mainMenuLocalStorageKey)
      const prevValue = prevValueStr && JSON.parse(prevValueStr)
      const obj = prevValue
        ? {...prevValue, menuOpen: isOpened}
        : {menuOpen: isOpened}

      window.localStorage.setItem(mainMenuLocalStorageKey, JSON.stringify(obj))
    }, 0)
  }
}

const mainMenuStore = new MainMenuStore()
const [MainMenuContext, useMainMenuStore] = createUseStore(MainMenuStore)
export {MainMenuContext, useMainMenuStore, mainMenuStore}
